import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { useState } from 'react';

import { TSDListItem } from '@e-origin/contracts';
import { TSDStatus } from '@e-origin/shared';

import { useChangeTsdStatus } from '@admin/hooks';
import { Select } from '@components';
import { getStatusLabel } from '@features/tsd/utils';

interface ChangeTsdStatusProps {
  onClose: () => void;
  tsd: TSDListItem;
}

const changeTsdStatusSchema = z.object({
  status: z.nativeEnum(TSDStatus),
});

export const ChangeTsdStatus: React.FC<ChangeTsdStatusProps> = ({ onClose, tsd }) => {
  const changeTsdStatusMutation = useChangeTsdStatus();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      status: tsd.generalInfo.status,
    },
    validate: zodResolver(changeTsdStatusSchema),
    validateInputOnBlur: true,
  });

  const statusOptions = Object.values(TSDStatus).map((status) => ({
    value: status,
    label: getStatusLabel(status),
  }));

  const handleSubmit = async (values: typeof form.values) => {
    setFormError(null);

    try {
      await changeTsdStatusMutation.mutateAsync({
        status: values.status,
        tsdId: tsd._id,
      });

      onClose();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to update TSD status');
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" mt="xl">
        <Select label="Status" placeholder="Select status" data={statusOptions} {...form.getInputProps('status')} searchable />

        {formError && (
          <Text c="red" fw="bold">
            {formError}
          </Text>
        )}

        <Group justify="center" mt="md">
          <Button type="button" onClick={onClose} disabled={changeTsdStatusMutation.isPending} variant="transparent">
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={changeTsdStatusMutation.isPending || !form.isValid()}
            leftSection={changeTsdStatusMutation.isPending && <Loader color="white" size="xs" />}
          >
            Change Status
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
